<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-university me-2"></i> {{ $t('bank.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('bank.bankCode') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('bank.bankName') }}</label>
              <input type="text" class="form-control" v-model="form.name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded d-flex align-items-center justify-content-between">
              <label class="form-label mb-0">{{ $t('bank.status') }}</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" v-model="form.is_active" />
                <label class="form-check-label">
                  {{ form.is_active ? $t('bank.active') : $t('bank.inactive') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('bank.update') : $t('bank.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('bank.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('bank.existingBanks') }}
      </h5>

      <div v-if="banks.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('bank.code') }}</th>
              <th>{{ $t('bank.name') }}</th>
              <th>{{ $t('bank.status') }}</th>
              <th class="text-center">{{ $t('bank.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bank in banks" :key="bank.id">
              <td>{{ bank.code }}</td>
              <td>{{ bank.name }}</td>
              <td>
                <span :class="bank.is_active ? 'text-success' : 'text-danger'">
                  {{ bank.is_active ? $t('bank.active') : $t('bank.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadBank(bank)">{{ $t('bank.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm" @click="deleteBank(bank.id)">{{ $t('bank.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Contrôles de pagination -->
      <div v-if="lastPage > 1" class="d-flex justify-content-end mt-3">
        <nav>
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPreviousPage()" style="color: #b03a45; border-color: #b03a45;">
                <i class="fas fa-chevron-left"></i>
              </a>
            </li>
            
            <li v-for="page in Math.min(lastPage, 5)" :key="page" 
                class="page-item" 
                :class="{ active: page === currentPage }">
              <a class="page-link" href="#" @click.prevent="goToPage(page)" 
                 :style="page === currentPage ? 'background-color: #b03a45; border-color: #b03a45;' : 'color: #b03a45; border-color: #b03a45;'">
                {{ page }}
              </a>
            </li>
            
            <li v-if="lastPage > 5" class="page-item disabled">
              <span class="page-link" style="color: #b03a45; border-color: #b03a45;">...</span>
            </li>
            
            <li v-if="lastPage > 5" class="page-item" :class="{ active: currentPage === lastPage }">
              <a class="page-link" href="#" @click.prevent="goToPage(lastPage)"
                 :style="currentPage === lastPage ? 'background-color: #b03a45; border-color: #b03a45;' : 'color: #b03a45; border-color: #b03a45;'">
                {{ lastPage }}
              </a>
            </li>
            
            <li class="page-item" :class="{ disabled: currentPage === lastPage }">
              <a class="page-link" href="#" @click.prevent="goToNextPage()" style="color: #b03a45; border-color: #b03a45;">
                <i class="fas fa-chevron-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
      
      <p v-else class="text-muted">{{ $t('bank.noBanks') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import bankService from '@/services/bankService'

const { t } = useI18n()
const toast = useToast()
const banks = ref([])
const editingId = ref(null)

// Variables pour la pagination
const currentPage = ref(1)
const lastPage = ref(1)
const perPage = ref(10)
const total = ref(0)

const form = ref({
  code: '',
  name: '',
  is_active: true
})

const fetchBanks = async (page = 1) => {
  console.log('fetchBanks called for page:', page)
  console.log('Token from localStorage:', localStorage.getItem('token'))
  
  try {
    const response = await bankService.getAll(page)
    console.log('API Response:', response)
    console.log('Response data:', response.data)
    console.log('Response status:', response.status)
    
    // Gère les différents formats de réponse possibles
    let banksData = []
    let paginationData = {}
    
    if (response.data) {
      if (response.data.data && Array.isArray(response.data.data)) {
        // Format paginé Laravel
        banksData = response.data.data
        paginationData = {
          currentPage: response.data.current_page,
          lastPage: response.data.last_page,
          perPage: response.data.per_page,
          total: response.data.total
        }
        console.log('Using paginated format')
      } else if (Array.isArray(response.data)) {
        // Format direct (non paginé)
        banksData = response.data
        paginationData = {
          currentPage: 1,
          lastPage: 1,
          perPage: banksData.length,
          total: banksData.length
        }
        console.log('Using direct array format')
      } else if (response.data.banks && Array.isArray(response.data.banks)) {
        // Format avec clé 'banks'
        banksData = response.data.banks
        paginationData = {
          currentPage: response.data.current_page || 1,
          lastPage: response.data.last_page || 1,
          perPage: response.data.per_page || banksData.length,
          total: response.data.total || banksData.length
        }
        console.log('Using banks key format')
      } else {
        console.warn('Unknown response format:', response.data)
      }
    }
    
    // Mettre à jour les données
    banks.value = banksData
    currentPage.value = paginationData.currentPage || 1
    lastPage.value = paginationData.lastPage || 1
    perPage.value = paginationData.perPage || 10
    total.value = paginationData.total || 0
    
    console.log('Banks data extracted:', banksData)
    console.log('Pagination info:', {
      currentPage: currentPage.value,
      lastPage: lastPage.value,
      perPage: perPage.value,
      total: total.value
    })
  } catch (err) {
    console.error('Error in fetchBanks:', err)
    console.error('Error status:', err.response?.status)
    console.error('Error message:', err.response?.data?.message || err.message)
    console.error('Request headers:', err.config?.headers)
    console.error('Full error response:', err.response)
    
    if (err.response?.status === 403) {
      toast.error('Permission denied: You do not have the required role to access banks')
      console.error('403 Error - Check user role and token validity')
    } else {
      toast.error(t('bank.failedToLoad'))
    }
  }
}

// Fonctions de navigation pour la pagination
const goToPage = (page) => {
  if (page >= 1 && page <= lastPage.value) {
    fetchBanks(page)
  }
}

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    fetchBanks(currentPage.value - 1)
  }
}

const goToNextPage = () => {
  if (currentPage.value < lastPage.value) {
    fetchBanks(currentPage.value + 1)
  }
}

const handleSubmit = async () => {
  try {
    console.log('Form data being sent:', form.value)
    
    if (editingId.value) {
      await bankService.update(editingId.value, form.value)
      toast.success(t('bank.bankUpdated'))
    } else {
      await bankService.create(form.value)
      toast.success(t('bank.bankAdded'))
    }
    resetForm()
    console.log('About to call fetchBanks after successful operation')
    fetchBanks()
  } catch (err) {
    console.error('API Error:', err)
    
    // Show detailed error information
    if (err.response) {
      console.error('Response data:', err.response.data)
      console.error('Response status:', err.response.status)
      
      if (err.response.status === 422) {
        // Validation error - show specific field errors
        const errors = err.response.data.errors || err.response.data.message || 'Validation error'
        toast.error(`Validation error: ${JSON.stringify(errors)}`)
      } else {
        toast.error(`${t('bank.errorSaving')}: ${err.response.data?.message || err.message}`)
      }
    } else if (err.request) {
      toast.error(`${t('bank.errorSaving')}: Network error`)
    } else {
      toast.error(`${t('bank.errorSaving')}: ${err.message}`)
    }
  }
}

const loadBank = (bank) => {
  editingId.value = bank.id
  form.value = {
    code: bank.code,
    name: bank.name,
    is_active: bank.is_active
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    name: '',
    is_active: true
  }
}

const deleteBank = async (id) => {
  if (!confirm(t('bank.confirmDelete'))) return
  try {
    await bankService.delete(id)
    toast.success(t('bank.bankDeleted'))
    fetchBanks()
  } catch (err) {
    toast.error(t('bank.failedToDelete'))
    console.error(err)
  }
}

onMounted(() => {
  fetchBanks()
})
</script>
